import { createContext, useContext, useState, type ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { usePublishImpersonationTarget } from "@/contexts/ImpersonationTargetContext";
import type { PersonaVerComo } from "@/lib/verComoPersonas";

/**
 * "Ver como" del shell del ADMIN (rutas que pinta `AdminSidebar`, no los portales).
 *
 * Quien puede impersonar elige a una persona con acceso al admin (`get_ver_como_personas` con
 * `/admin/%`) y ve los menus de SU rol. Es solo presentacion: la autorizacion sigue con el rol
 * real (`useDynamicMenus` / `useAllowedMenus`). Mientras hay persona se publica el target global
 * (solo lectura + cabecera `x-ver-como`). El provider se monta solo en el ramal admin de
 * `AdminLayoutPortal`: al salir a un portal se desmonta y deja de publicar.
 */

interface AdminVerComoContextType {
  /** Persona elegida en "Ver como"; null = vista propia. */
  persona: PersonaVerComo | null;
  setPersona: (p: PersonaVerComo | null) => void;
}

const STORAGE_KEY = "sozu-admin-ver-como-persona";

const AdminVerComoContext = createContext<AdminVerComoContextType>({
  persona: null,
  setPersona: () => {},
});

function leerPersonaGuardada(): PersonaVerComo | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const p = raw ? (JSON.parse(raw) as PersonaVerComo) : null;
    return p?.authUserId && p?.email ? p : null;
  } catch {
    return null;
  }
}

export function AdminVerComoProvider({ children }: { children: ReactNode }) {
  const [personaElegida, setPersonaState] = useState<PersonaVerComo | null>(leerPersonaGuardada);
  const { profile } = useAuth();

  // Solo quien puede impersonar tiene persona elegida.
  const persona = profile?.puede_impersonar === true ? personaElegida : null;

  usePublishImpersonationTarget(
    persona
      ? {
          email: persona.email,
          personaId: persona.personaId,
          nombre: persona.nombre,
          rolId: persona.rolAccesoId,
          rolNombre: persona.rolAccesoNombre,
        }
      : null
  );

  const setPersona = (p: PersonaVerComo | null) => {
    setPersonaState(p);
    try {
      if (p == null) window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
    } catch {
      /* noop */
    }
  };

  return (
    <AdminVerComoContext.Provider value={{ persona, setPersona }}>
      {children}
    </AdminVerComoContext.Provider>
  );
}

export function useAdminVerComo() {
  return useContext(AdminVerComoContext);
}
